import { getAssetScanTrackingReportByTagId } from "../../Apis/rfidApi";
import { mapRowToReport } from "./RfidReportsExpandableTable";
import {
  getReportRowKey,
  mapScannedTagData,
} from "./store/useRfidReportsStore";

export const RFID_REPORTS_EXPORT_COLUMNS = [
  "S.No",
  "Asset Code",
  "Asset Type",
  "Asset Category",
  "IMEI/SL/MFG",
  "Tag Vehicle No",
  "Zone",
  "Ward",
  "Scanned By Reader",
  "Reader Category",
  "Reader Vehicle",
  "Reader Zone",
  "Reader Ward",
  "Total Scanned",
  "Actual Shift",
  "Last Scanned Time",
  "In Time",
  "Out Time",
];

export const SCAN_DETAIL_EXPORT_COLUMNS = [
  "S.No",
  "Tag ID",
  "Reader ID",
  "Scanned On",
  "Actual Shift",
  "Reader Installed Area",
  "Reader Vehicle",
  "Reader Category",
  "Reader Zone",
  "Reader Ward",
  "Status",
];

export const getExportDateTime = () => {
  const now = new Date();
  const date = `${now.getDate()}-${now.getMonth() + 1}-${now.getFullYear()}`;
  const time = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;
  return `RFID List Generated on: ${date}, ${time}`;
};

export const getScannedTagsForReportRow = (row, scannedTagsByKey = {}) => {
  const rowKey = getReportRowKey(row);
  if (rowKey && scannedTagsByKey[rowKey]?.length) {
    return scannedTagsByKey[rowKey];
  }
  if (row?.scanDetails?.length) return row.scanDetails;
  return [];
};

export const loadAllReportScannedTags = async (
  rfidReportData = [],
  filters = {},
  cachedTags = {}
) => {
  const scannedTagsByKey = {};

  await Promise.all(
    rfidReportData.map(async (row) => {
      const rowKey = getReportRowKey(row);
      const imeiNo =
        row?.imeiNo ?? row?.imeiSlMfgNo ?? row?.imeiOrSerialOrMfgNo;
      if (!rowKey || !imeiNo || imeiNo === "N/A") return;

      if (cachedTags[rowKey]?.length) {
        scannedTagsByKey[rowKey] = cachedTags[rowKey];
        return;
      }

      try {
        const response = await getAssetScanTrackingReportByTagId({ imeiNo });
        const tagList = Array.isArray(response?.data) ? response.data : [];
        scannedTagsByKey[rowKey] = mapScannedTagData(tagList);
      } catch {
        scannedTagsByKey[rowKey] = row?.scanDetails ?? [];
      }
    })
  );

  return scannedTagsByKey;
};

export const mapScanDetailRowsForExport = (scanDetails = []) =>
  scanDetails.map((item, index) => [
    index + 1,
    item.tagId || "N/A",
    item.readerId || "N/A",
    item.scannedOn || "N/A",
    item.actualShift || "N/A",
    item.readerInstalledArea || "N/A",
    item.readerVehicle || "N/A",
    item.readerCategory || "N/A",
    item.readerZone || "N/A",
    item.readerWard || "N/A",
    item.statusLabel ||
      (typeof item.status === "string" ? item.status : null) ||
      ((item.isActive ?? true) ? "Active" : "Inactive"),
  ]);

export const formatExportDateTime = (value) => {
  if (!value) return "N/A";
  const raw = String(value).trim();
  if (!raw) return "N/A";
  const normalized =
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw) ? raw.replace(" ", "T") : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const buildReportExportData = (rfidReportData = [], scannedTagsByKey = {}) =>
  (rfidReportData ?? []).map((row, index) => {
    const scannedTags = getScannedTagsForReportRow(row, scannedTagsByKey);
    const item = mapRowToReport(row, index, scannedTags);
    const serialNo = row?.rowNumber ?? index + 1;

    return {
      mainRow: [
        serialNo,
        item.rfidCode || "N/A",
        item.assetTypeName || "N/A",
        item.rfidCategoryName || "N/A",
        item.imeiSlMfgNo || "N/A",
        item.vehicleNo || "N/A",
        item.zoneName || "N/A",
        item.wardNo || "N/A",
        item.scannedByReader || "N/A",
        item.readerCategory || "N/A",
        item.readerVehicle || "N/A",
        item.readerZone || "N/A",
        item.readerWard || "N/A",
        item.totalScanned ?? "N/A",
        item.actualShift || "N/A",
        formatExportDateTime(item.lastScannedOn),
        formatExportDateTime(item.inTime),
        formatExportDateTime(item.outTime),
      ],
      scanDetails: item.scanDetails ?? [],
      rfidCode: item.rfidCode || "N/A",
    };
  });
